import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, XCircle, Eye, EyeOff, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { getApiClient, api } from "../lib/api";
import AdminFilters from "../components/filters/AdminFilters";
import AdminPagination from "../components/pagination/AdminPagination";
import ConfirmDialog from "../components/ConfirmDialog";
import { useAdminFilters } from "../hooks/useAdminFilters";
import { useModalScrollLock } from "../hooks/useModalScrollLock";
import { handleApiError } from "../utils/errorHandler";

const apiClient = getApiClient();

interface Review {
  id: string;
  productName?: string;
  customerName?: string;
  rating: number;
  title: string;
  comment: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "HIDDEN";
  isVerifiedPurchase: boolean;
  createdAt: string;
  user: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  product: {
    name: string;
  };
}

interface DashboardStats {
  pending: number;
  approved: number;
  rejected: number;
  hidden: number;
  averageRating: number;
}

export default function Reviews() {
  const filterManager = useAdminFilters({ defaultLimit: 10, initialFilters: { status: 'PENDING' } });
  const { search, debouncedSearch, page, limit, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager;

  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useModalScrollLock(showDetails);

  const status = filterManager.filters.status || '';

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [page, limit, debouncedSearch, JSON.stringify(filterManager.filters)]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await api.getReviews(page, limit, status, debouncedSearch, filterManager.filters);
      setReviews(response.data?.data || []);
      setTotal(response.data?.pagination?.total || 0);
    } catch (error) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/admin/reviews/dashboard/stats");
      setStats(response.data?.data || null);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await api.approveReview(id);
      toast.success("Review approved");
      fetchReviews();
      fetchStats();
    } catch (error) {
      handleApiError(error, "Failed to approve review");
    }
  };

  const handleReject = async (id: string) => {
    try {
      await api.rejectReview(id);
      toast.success("Review rejected");
      fetchReviews();
      fetchStats();
    } catch (error) {
      handleApiError(error, "Failed to reject review");
    }
  };

  const handleHide = async (id: string) => {
    try {
      await api.hideReview(id);
      toast.success("Review hidden");
      fetchReviews();
      fetchStats();
    } catch (error) {
      handleApiError(error, "Failed to hide review");
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; reviewId: string | null; isLoading: boolean }>({
    isOpen: false,
    reviewId: null,
    isLoading: false,
  });

  const handleDeleteClick = (id: string) => {
    setDeleteConfirm({ isOpen: true, reviewId: id, isLoading: false });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.reviewId) return;

    try {
      setDeleteConfirm((prev) => ({ ...prev, isLoading: true }));
      await api.deleteReview(deleteConfirm.reviewId);
      toast.success("Review deleted successfully");
      fetchReviews();
      fetchStats();
      setDeleteConfirm({ isOpen: false, reviewId: null, isLoading: false });
    } catch (error) {
      handleApiError(error, "Failed to delete review");
      setDeleteConfirm((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "HIDDEN":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Dashboard Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-400">
            <p className="text-sm text-gray-600">Pending</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-400">
            <p className="text-sm text-gray-600">Approved</p>
            <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-400">
            <p className="text-sm text-gray-600">Rejected</p>
            <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
            <p className="text-sm text-gray-600">Hidden</p>
            <p className="text-3xl font-bold text-gray-600">{stats.hidden}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
            <p className="text-sm text-gray-600">Avg Rating</p>
            <p className="text-3xl font-bold text-blue-600">{stats.averageRating}⭐</p>
          </div>
        </div>
      )}

      {/* Header and Filters */}
      <div className="space-y-4">
        <h1 className="text-3xl font-bold text-[#6D3A0D]">Product Reviews</h1>

        <AdminFilters
          search={search}
          searchPlaceholder="Search reviews by title or comment..."
          onSearchChange={setSearch}
          filters={filterManager.filters}
          onFilterChange={setFilter}
          onRemoveFilter={removeFilter}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          isLoading={loading}
          fields={[
            {
              key: 'status',
              label: 'Review Status',
              type: 'select',
              options: [
                { label: 'Pending', value: 'PENDING' },
                { label: 'Approved', value: 'APPROVED' },
                { label: 'Rejected', value: 'REJECTED' },
                { label: 'Hidden', value: 'HIDDEN' },
              ],
            },
          ]}
        />
      </div>

      {/* Reviews Table */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D3A0D]"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-lg border-2 border-dashed border-[#F6EEE7] p-12 text-center">
          <p className="text-gray-500 text-lg">No reviews found</p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#6D3A0D] underline hover:text-[#5D2A0D]"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg border-2 border-[#F6EEE7] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-[#F6EEE7]">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#6D3A0D]">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#6D3A0D]">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#6D3A0D]">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#6D3A0D]">
                      Review Content
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-[#6D3A0D]">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-[#6D3A0D]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F6EEE7]">
                  {reviews.map((review) => (
                    <tr key={review.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                        {review.product?.name || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="font-medium text-gray-900">{review.user?.email || "N/A"}</div>
                        {review.isVerifiedPurchase && (
                          <span className="text-[10px] font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded-full mt-1 inline-block border border-green-200">
                            ✓ Verified
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-yellow-600">
                        {"⭐".repeat(review.rating)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-sm">
                        <div className="font-bold text-gray-900 line-clamp-1">{review.title || "No Title"}</div>
                        <div className="text-xs text-gray-600 line-clamp-2 mt-0.5">{review.comment}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold inline-block ${getStatusColor(review.status)}`}>
                          {review.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex gap-1.5 justify-center flex-wrap">
                          <button
                            onClick={() => {
                              setSelectedReview(review);
                              setShowDetails(true);
                            }}
                            className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {review.status !== 'APPROVED' && (
                            <button
                              onClick={() => handleApprove(review.id)}
                              className="p-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                              title="Approve Review"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}

                          {review.status !== 'REJECTED' && (
                            <button
                              onClick={() => handleReject(review.id)}
                              className="p-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
                              title="Reject Review"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}

                          {review.status !== 'HIDDEN' && (
                            <button
                              onClick={() => handleHide(review.id)}
                              className="p-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                              title="Hide Review"
                            >
                              <EyeOff className="w-4 h-4" />
                            </button>
                          )}

                          <button
                            onClick={() => handleDeleteClick(review.id)}
                            className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                            title="Delete Review"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="mt-6">
              <AdminPagination
                page={page}
                limit={limit}
                total={total}
                onPageChange={setPage}
                onLimitChange={setLimit}
                isLoading={loading}
                showInfo={true}
              />
            </div>
          )}
        </>
      )}

      {/* Review Details Modal */}
      {showDetails && selectedReview && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <h2 className="text-2xl font-bold text-[#6D3A0D] mb-4">Review Details</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Product</p>
                  <p className="font-semibold text-gray-900">{selectedReview.product?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Customer Email</p>
                  <p className="font-semibold text-gray-900">{selectedReview.user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Rating</p>
                  <p className="font-semibold text-yellow-600">{"⭐".repeat(selectedReview.rating)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`px-3 py-1 rounded text-xs font-semibold inline-block ${getStatusColor(selectedReview.status)}`}>
                    {selectedReview.status}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-2">Title</p>
                <p className="font-semibold text-gray-900">{selectedReview.title}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-2">Review</p>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{selectedReview.comment}</p>
              </div>

              {selectedReview.isVerifiedPurchase && (
                <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded">
                  <p className="text-sm text-green-800">✓ Verified Purchase</p>
                </div>
              )}

              <div className="bg-gray-50 p-3 rounded text-sm text-gray-600">
                Submitted on {new Date(selectedReview.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>

              {selectedReview.status === "PENDING" && (
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      handleApprove(selectedReview.id);
                      setShowDetails(false);
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      handleReject(selectedReview.id);
                      setShowDetails(false);
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {selectedReview.status !== "PENDING" && (
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      handleDeleteClick(selectedReview.id);
                      setShowDetails(false);
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Review"
        message="Are you sure you want to delete this customer review? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        isLoading={deleteConfirm.isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, reviewId: null, isLoading: false })}
      />
    </div>
  );
}
